import { START_LOCATION } from 'vue-router';

import router from '@/core/router';

// Single-flight: while a query keeps failing with a dead session, the error
// link fires on every retry before the redirect lands. Without the latch each
// call queues another navigation, which re-runs the auth guard, which fetches
// `me`, which fails again — a loop.
let loginRedirectInFlight = false;

/** Send the user to sign in, coming back to where they are now. */
export const goToLogin = (): void => {
  // Before the first navigation lands there is no "here" to come back to, and
  // a redirect would cancel that navigation; the guard owns that moment.
  if (loginRedirectInFlight || router.currentRoute.value === START_LOCATION || router.currentRoute.value.name === 'Login') {
    return;
  }

  loginRedirectInFlight = true;

  void router.replace({
    name: 'Login',
    query: { redirect_uri: router.currentRoute.value.fullPath },
  }).finally(() => {
    loginRedirectInFlight = false;
  });
};

/** The API's REST sign-out endpoint (clears the HttpOnly session cookies). */
export const getLogoutUrl = (): string => `${import.meta.env.VITE_OMB_API_URL}/auth/logout`;

/**
 * An in-app path to return to after sign-in, or `null`. Only a single string
 * (not a repeated `?redirect_uri=`, which arrives as an array) that resolves to
 * this origin counts — parsed as the browser would, so `//host`, `/\\host` and
 * `/<tab>/host` are refused even if a later caller hands the result to
 * `window.location` instead of the router.
 */
export const safeRedirectPath = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return null;
  }

  try {
    const url = new URL(value, window.location.origin);

    return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
};

/**
 * The API's Google sign-in for this app. `client=app` makes the API send the
 * browser back here — at the URL it is configured with, never one from the
 * request — and to `redirectUri` once signed in (only a safe in-app path is
 * passed on).
 */
export const getGoogleSignInUrl = (redirectUri?: unknown): string => {
  const params = new URLSearchParams({ client: 'app' });
  const path = safeRedirectPath(redirectUri);

  if (path) {
    params.set('redirect_uri', path);
  }

  return `${import.meta.env.VITE_OMB_API_URL}/auth/google/start?${params.toString()}`;
};
